import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { cx } from '../../lib/cx';
import './file-name.css';

/** How long the pointer rests on a cut-short name before its tooltip comes up. */
const HOVER_DELAY_MS = 350;

/**
 * File names on one line, cut short with an ellipsis where there isn't room; pointed at or
 * focused, a tooltip shows them whole. `label` stands in for several, such as "2 images", and its
 * tooltip lists them, a line each.
 */
export function FileName({
  names,
  label,
}: {
  readonly names: readonly string[];
  readonly label?: string | undefined;
}) {
  const text = label ?? names.join(', ');
  const textRef = useRef<HTMLSpanElement>(null);
  const [cut, setCut] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const id = useId();
  // Several names are always behind their label; one, only when it doesn't fit.
  const hidden = label !== undefined || cut;

  useLayoutEffect(() => {
    const node = textRef.current;
    if (!node) return;
    const measure = () => {
      setCut(node.scrollWidth > node.clientWidth);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [text]);
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
    },
    [],
  );

  function show(delay: number) {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setOpen(true);
    }, delay);
  }
  function hide() {
    window.clearTimeout(timer.current);
    setOpen(false);
  }

  return (
    <span
      className="file-name"
      // Focusable only while there's more to show, so the keyboard can bring the tooltip up too.
      tabIndex={hidden ? 0 : undefined}
      aria-describedby={hidden ? id : undefined}
      onPointerEnter={() => {
        if (hidden) show(HOVER_DELAY_MS);
      }}
      onPointerLeave={hide}
      onFocus={() => {
        if (hidden) show(0);
      }}
      onBlur={hide}
      onKeyDown={(event) => {
        // Esc puts the tooltip away first, before it means anything else.
        if (event.key !== 'Escape' || !open) return;
        event.stopPropagation();
        hide();
      }}
    >
      <span ref={textRef} className="file-name-text">
        {text}
      </span>
      {hidden && (
        <span id={id} role="tooltip" className={cx('file-name-tooltip', open && 'is-open')}>
          {names.map((name, index) => (
            <span key={`${String(index)}:${name}`}>{name}</span>
          ))}
        </span>
      )}
    </span>
  );
}
